import { DM_OFFER_TOKEN } from './dm-template';

/**
 * Where a Comment → DM button opens. CAMPAIGN = one of the salon's campaign landing pages, CUSTOM = a link the salon
 * typed, and three salon pages the API resolves when each DM is sent (never from the browser): the New Client Offer
 * page, the Bio Page and the booking link. A salon page that is down falls back like Text Keywords and flyer QR codes:
 * offer → Bio Page → booking link.
 */

export const COMMENT_AUTOMATION_DM_BUTTON_KINDS = ['CAMPAIGN', 'CUSTOM', 'NEW_CLIENT_OFFER', 'BIO_PAGE', 'ONLINE_BOOKING'] as const;
export type CommentAutomationDmButtonKind = (typeof COMMENT_AUTOMATION_DM_BUTTON_KINDS)[number];

/** The kinds whose URL is the salon's own page, in picker order. */
export const DM_BUTTON_SALON_PAGE_KINDS = ['NEW_CLIENT_OFFER', 'BIO_PAGE', 'ONLINE_BOOKING'] as const;
export type DmButtonSalonPageKind = (typeof DM_BUTTON_SALON_PAGE_KINDS)[number];

export function isDmButtonSalonPageKind(kind: CommentAutomationDmButtonKind): kind is DmButtonSalonPageKind {
  return (DM_BUTTON_SALON_PAGE_KINDS as readonly string[]).includes(kind);
}

/** Picker / list words, the same as Text Keywords' "Send them to". */
export const DM_BUTTON_DESTINATION_LABELS: Record<CommentAutomationDmButtonKind, string> = {
  NEW_CLIENT_OFFER: 'New Client Offer',
  CAMPAIGN: 'A campaign',
  BIO_PAGE: 'Bio Page',
  ONLINE_BOOKING: 'Book online',
  CUSTOM: 'Custom link',
};

/** Why a salon page can't be a button at all (a paused offer still can: its DMs fall back). */
export const DM_BUTTON_SALON_PAGE_UNAVAILABLE_REASONS = [
  'SALON_LINK_UNAVAILABLE',
  'OFFER_NOT_SET_UP',
  'BIO_PAGE_NOT_PUBLISHED',
  'BOOKING_LINK_UNAVAILABLE',
  'BOOKING_LINK_NOT_HTTPS',
] as const;
export type DmButtonSalonPageUnavailableReason = (typeof DM_BUTTON_SALON_PAGE_UNAVAILABLE_REASONS)[number];

/** Plain reason, shown on the disabled picker option and in the save error. */
export const DM_BUTTON_SALON_PAGE_UNAVAILABLE_MESSAGES: Record<DmButtonSalonPageUnavailableReason, string> = {
  SALON_LINK_UNAVAILABLE: 'Your salon’s web address isn’t set up yet',
  OFFER_NOT_SET_UP: 'Set up your New Client Offer first',
  BIO_PAGE_NOT_PUBLISHED: 'Publish your Bio Page first',
  BOOKING_LINK_UNAVAILABLE: 'Turn on online booking or save a booking link first',
  BOOKING_LINK_NOT_HTTPS: 'Your booking link must start with https:// to open from a message',
};

/** Where a button's DMs go while its own page is down: the Bio Page, else the booking link. */
export const DM_BUTTON_FALLBACKS = ['BIO_PAGE', 'BOOKING'] as const;
export type DmButtonFallback = (typeof DM_BUTTON_FALLBACKS)[number];

/**
 * Suggested button label when a salon page is picked and the label is still empty (≤ 20 chars, Meta's limit). The
 * offer button says "Claim {offer}", filled from the live offer at send time ("Claim $10 off"; "See our page" while the
 * offer is off), so the label never goes stale; without any offer set up there is nothing to name.
 */
export function defaultDmButtonLabel(kind: DmButtonSalonPageKind, hasOffer: boolean): string {
  switch (kind) {
    case 'NEW_CLIENT_OFFER':
      return hasOffer ? `Claim ${DM_OFFER_TOKEN}` : 'Claim your offer';
    case 'BIO_PAGE':
      return 'See our page';
    case 'ONLINE_BOOKING':
      return 'Book now';
  }
}
